import dayjs from "@tether-web-portal/dayjs-setup";
import { getDeviceCapability } from "@tether-web-portal/api/catalog/deviceCatalogCache";

/**
 * Whether a device is reporting, decided from the device catalog's per-type silence tolerance.
 *
 * This is the portal's one answer to "is this device offline". It is deliberately NOT a boolean:
 * the call sites disagree about what a MISSING timestamp means, and each disagreement is
 * load-bearing — a room with no reading should not claim to be live, while a newly provisioned
 * device with no sample should not be listed as something to go and fix. Returning `unknown` keeps
 * that decision at the call site, written down, instead of buried in this function's default.
 *
 * Plain functions rather than hooks: the existing callers are non-component modules
 * (`makeSampleBreakdown`, `getSortedRoomLocations`, sorting comparators). They read the catalog
 * through the synchronous module cache that `useDeviceCatalog` keeps fresh during render.
 */
export type OnlineState = "online" | "offline" | "unknown";

export const DEFAULT_OFFLINE_AFTER_MINUTES = 120;

/**
 * Best-known tolerances, applied until the catalog has loaded and for any type the catalog does not
 * carry. Mostly this reproduces the timeout map the helper replaced, so a page rendering before the
 * network settles behaves as the old code did — worth keeping permanently for that reason.
 *
 * GENUS_INTELLILOG is the exception, and deliberately so: 26 h is a NEW value, not an old one. It
 * is listed here because the catalog is served `max-age=3600`, so for up to an hour after an API
 * deploy a browser can still answer from a cached v5 catalog with no `reporting` block — and
 * without this entry a once-daily logger would keep reading offline through exactly that window.
 *
 * These numbers are the catalog's, and today they carry every tolerance the catalog serves. Keep it
 * that way until the callers are reactive: they read the module cache synchronously and are not
 * catalog subscribers (AppLayout's `useDeviceCatalog` re-renders its own tree, not the page passed in
 * as `children`, and `SortedRoomLocations` memoizes without a catalog dependency). So a page that
 * renders before the catalog loads keeps the fallback answer until it next re-renders — for a
 * memoized caller, until its own inputs change. A served tolerance that differs from this map is
 * therefore wrong on first paint until then. Device-offline phase 3 (new per-type numbers) makes
 * the callers subscribe before it changes any value.
 */
const FALLBACK_OFFLINE_AFTER_MINUTES: Record<string, number> = {
  DETECTIFY: 60 * 9,
  BOVE_BECO: 60 * 6, // reports 4-hourly
  GENUS_INTELLILOG: 60 * 26, // reports once daily; 24 h cadence + 2 h grace
};

const fallbackOfflineAfterMinutes = (deviceType?: string): number =>
  // Device type arrives lowercase from the property info API.
  FALLBACK_OFFLINE_AFTER_MINUTES[deviceType?.toUpperCase() ?? ""] ?? DEFAULT_OFFLINE_AFTER_MINUTES;

/** How long this device type may be silent before it counts as offline, in minutes. */
export const offlineAfterMinutes = (deviceType?: string): number =>
  getDeviceCapability(deviceType)?.reporting?.offlineAfterMinutes ??
  fallbackOfflineAfterMinutes(deviceType);

/**
 * `unknown` when there is no usable timestamp — never coerced to "now". `dayjs(undefined)` is the
 * current time, so an absent timestamp compared that way silently reports a device that has never
 * transmitted as online.
 *
 * Silence is compared strictly (a device exactly on its tolerance is still online, so one
 * reporting punctually on its own cadence does not flicker on the boundary) and in MILLISECONDS,
 * not truncated minutes. The rule this replaced used `dayjs().diff(t, "minute")`, which truncates,
 * so "offline after 120 minutes" actually fired at 121. The API-side helper compares milliseconds;
 * two implementations of one rule disagreeing by up to 59 seconds is the drift this whole change
 * exists to remove, so both round-trip the same way and a device now crosses at exactly its
 * tolerance.
 */
export const deviceOnlineState = (
  lastSeenAt: string | number | Date | undefined | null,
  deviceType?: string,
): OnlineState => {
  const silence = silenceMs(lastSeenAt);
  if (silence === undefined) return "unknown";

  return silence > toleranceMs(deviceType) ? "offline" : "online";
};

/**
 * A colour ramp needs more boundaries than "online or not", so this derives them from the same one
 * catalog number rather than serving extra fields:
 *
 * - `fresh`   silence <= tolerance — exactly when `deviceOnlineState` says `online`
 * - `stale`   tolerance < silence <= DEAD_AFTER_TOLERANCES x tolerance
 * - `dead`    silence > DEAD_AFTER_TOLERANCES x tolerance
 * - `unknown` no usable timestamp, by the same rule as `deviceOnlineState` — render grey, never green
 *
 * Both functions read the same silence and the same tolerance, so they cannot disagree about where
 * `fresh` ends.
 */
export type Freshness = "fresh" | "stale" | "dead" | "unknown";

/**
 * How many tolerances of silence before a device is `dead` rather than `stale`. A product choice, not
 * a measured one, and pending ops sign-off: it decides when a surface turns red (a 2-hour type goes
 * red at 6 hours), so change it deliberately and in all three clients at once.
 */
export const DEAD_AFTER_TOLERANCES = 3;

export const deviceFreshness = (
  lastSeenAt: string | number | Date | undefined | null,
  deviceType?: string,
): Freshness => {
  const silence = silenceMs(lastSeenAt);
  if (silence === undefined) return "unknown";

  const tolerance = toleranceMs(deviceType);
  if (silence <= tolerance) return "fresh";
  return silence <= tolerance * DEAD_AFTER_TOLERANCES ? "stale" : "dead";
};

const toleranceMs = (deviceType?: string): number => offlineAfterMinutes(deviceType) * 60_000;

/** Milliseconds since `lastSeenAt`, or undefined when there is no usable timestamp. One clock read. */
const silenceMs = (lastSeenAt: string | number | Date | undefined | null): number | undefined => {
  if (lastSeenAt === undefined || lastSeenAt === null || lastSeenAt === "") return undefined;

  const lastSeen = dayjs(lastSeenAt);
  return lastSeen.isValid() ? dayjs().diff(lastSeen) : undefined;
};
